import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
} from "@angular/core";
import {ActivatedRoute, Router} from "@angular/router";
import {TranslateModule} from "@ngx-translate/core";
import {defer, finalize, Subscription, switchMap} from "rxjs";
import {ToastService} from "src/app/modules/core/services/toast.service";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {SocialLoginProvider} from "../../models/User";
import {AuthService} from "../../services/auth.service";
import {SocialAuthService} from "../../services/social-auth.service";
import {resolveAuthLocale} from "../../utils/auth-locale.util";

@Component({
  selector: "app-social-login-buttons",
  templateUrl: "./social-login-buttons.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
})
export class SocialLoginButtonsComponent implements OnDestroy {
  @Input() invitationToken?: string;
  @Input() disabled = false;
  @Input() termsVersion?: string;
  @Input() privacyVersion?: string;

  public loadingProvider?: SocialLoginProvider;
  public errorMessageKey?: string;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly authService: AuthService,
    private readonly socialAuthService: SocialAuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService
  ) {}

  public onSocialLogin(provider: SocialLoginProvider): void {
    if (this.loadingProvider || this.disabled) {
      return;
    }

    const locale = resolveAuthLocale();
    this.errorMessageKey = undefined;
    this.loadingProvider = provider;
    this.cdr.markForCheck();

    const socialLoginSubscription = defer(() =>
      this.socialAuthService.signIn(provider)
    )
      .pipe(
        switchMap((idToken) =>
          this.authService.socialLogin({
            idToken,
            provider,
            language: locale.language,
            country: locale.country,
            invitationToken: this.invitationToken,
            termsVersion: this.termsVersion,
            privacyVersion: this.privacyVersion,
          })
        ),
        switchMap(() => this.navigateAfterLogin()),
        finalize(() => {
          this.loadingProvider = undefined;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        error: (error: unknown) => {
          this.errorMessageKey = this.getErrorMessageKey(error);
          this.cdr.markForCheck();
          this.logClientError(provider, error);
          this.toastService.sendToast({
            text: this.errorMessageKey,
            classes: "bg-form-error",
            durationMs: 5000,
          });
        },
      });

    this.subscriptions.push(socialLoginSubscription);
  }

  private async navigateAfterLogin(): Promise<void> {
    const navigated = await this.router.navigateByUrl(this.getPostLoginUrl());

    if (!navigated) {
      throw new Error("SOCIAL_AUTH_NAVIGATION_FAILED");
    }
  }

  private getPostLoginUrl(): string {
    const redirectUrl = this.getRedirectUrl();

    if (this.authService.hasAcceptedLegalDocuments) {
      return redirectUrl;
    }

    return `/complete-signup?redirectTo=${encodeURIComponent(redirectUrl)}`;
  }
  private getRedirectUrl(): string {
    const redirectTo = this.route.snapshot.queryParamMap.get("redirectTo");

    if (!redirectTo?.startsWith("/") || redirectTo.startsWith("//")) {
      return "/";
    }

    return redirectTo;
  }

  private logClientError(provider: SocialLoginProvider, error: unknown): void {
    const payload = this.getClientErrorPayload(error);
    const logSubscription = this.authService
      .logSocialLoginClientError({
        provider,
        ...payload,
      })
      .subscribe({
        error: () => undefined,
      });

    this.subscriptions.push(logSubscription);
  }

  private getErrorMessageKey(error: unknown): string {
    const code = this.getErrorCode(error);

    switch (code) {
      case "FIREBASE_CONFIG_MISSING":
      case "auth/configuration-not-found":
      case "CONFIGURATION_NOT_FOUND":
        return "SOCIAL_AUTH_CONFIGURATION_ERROR";
      case "auth/popup-blocked":
        return "SOCIAL_AUTH_POPUP_BLOCKED";
      case "Your device doesn't support credential manager":
      case "Your device doesn't support credential manager.":
        return "SOCIAL_AUTH_GOOGLE_UNAVAILABLE_ON_DEVICE";
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
      case "popup-closed-by-user":
      case "cancelled-popup-request":
      case "Authorization canceled.":
      case "Sign in canceled.":
      case "The operation couldn’t be completed. (com.apple.AuthenticationServices.AuthorizationError error 1001.)":
        return "SOCIAL_AUTH_CANCELLED";
      case "auth/account-exists-with-different-credential":
        return "SOCIAL_AUTH_EMAIL_ALREADY_USED";
      case "SOCIAL_AUTH_TOKEN_INVALID":
      case "SOCIAL_AUTH_PROVIDER_INVALID":
      case "SOCIAL_AUTH_PROVIDER_MISMATCH":
      case "SOCIAL_AUTH_EMAIL_MISSING":
      case "SOCIAL_AUTH_EMAIL_NOT_VERIFIED":
      case "SOCIAL_AUTH_PROVIDER_ALREADY_USED":
      case "LEGAL_ACCEPTANCE_REQUIRED":
      case "LEGAL_VERSION_OUTDATED":
        return code;
      default:
        return "SOCIAL_AUTH_SIGNIN_FAILED";
    }
  }

  private getClientErrorPayload(error: unknown): {
    code?: string;
    message?: string;
    name?: string;
  } {
    const candidate = this.asErrorShape(error);

    if (error instanceof Error) {
      return {
        code: this.getErrorCode(error),
        message: error.message,
        name: error.name,
      };
    }

    return {
      code: this.getErrorCode(error),
      message:
        this.getStringValue(candidate?.message) ??
        this.getStringValue(this.asErrorShape(candidate?.error)?.message),
      name: this.getStringValue(candidate?.name),
    };
  }

  private getErrorCode(error: unknown): string | undefined {
    if (this.isCustomError(error)) {
      return error.error.error;
    }

    const candidate = this.asErrorShape(error);
    const nestedError = this.asErrorShape(candidate?.error);
    return (
      this.getStringValue(candidate?.code) ??
      this.getStringValue(nestedError?.code) ??
      this.getStringValue(nestedError?.error) ??
      this.getStringValue(nestedError?.message) ??
      this.getErrorMessageCode(error)
    );
  }

  private isCustomError(error: unknown): error is CustomError {
    const candidate = error as {error?: {error?: unknown}} | null;
    return (
      typeof candidate === "object" &&
      candidate !== null &&
      typeof candidate.error?.error === "string"
    );
  }

  private asErrorShape(error: unknown):
    | {
        code?: unknown;
        error?: unknown;
        message?: unknown;
        name?: unknown;
      }
    | undefined {
    return typeof error === "object" && error !== null
      ? (error as {
          code?: unknown;
          error?: unknown;
          message?: unknown;
          name?: unknown;
        })
      : undefined;
  }

  private getStringValue(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined;
  }

  private getErrorMessageCode(error: unknown): string | undefined {
    if (!(error instanceof Error)) {
      return undefined;
    }

    const firebaseCodeMatch = /\((auth\/[^)]+)\)/.exec(error.message);
    return firebaseCodeMatch?.[1] ?? error.message;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
